import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  MyCosmeticsSchema,
  type CosmeticKind,
  type MyCosmetics,
  type OwnedCosmetic,
} from "@legacy-manager/shared";
import { dataEHora, dataLonga, useIdioma, useT, type Chave, type Idioma } from "../i18n";
import { useErroEmToast } from "../ui/avisos";
import {
  CancelamentoDeAssinaturaSchema,
  CatalogoSchema,
  CobrancaSchema,
  PaginaDeRecibosSchema,
  type Catalogo,
  type Cobranca,
  type MetodoDePagamento,
  type ProdutoDaLoja,
  type Recibo,
  type TipoDeProduto,
} from "../online/esquemas";
import { ErroDaApi } from "../online/cliente";
import { mensagemDoErro, useSessao } from "../online/sessao";
import { Panel, Stat } from "../ui/atoms";
import { Pagina } from "../ui/Pagina";
import { PreviaDeCosmetico } from "../ui/cosmeticos";
import { abrirFora } from "../ui/abrirFora";

/**
 * Loja: licença vitalícia, Passe Legacy, cosméticos e recibos.
 *
 * Três decisões moldam esta tela.
 *
 * A primeira é o **servidor sem provedor de pagamento**. Ele responde 503 com
 * `BILLING_NOT_CONFIGURED`, e isso não é defeito nem culpa de quem está
 * jogando: é uma instalação que ainda não cadastrou credencial. A tela trata
 * como recado, com a mesma calma de um aviso, e continua mostrando o catálogo,
 * que é informação útil mesmo sem botão de compra. Transformar isso em tela
 * vermelha de erro ensinaria a pessoa a desconfiar de um servidor que está
 * perfeitamente saudável.
 *
 * A segunda é que **o direito nunca é decidido aqui**. Quem diz se o passe está
 * ativo é `GET /v1/me`, que a sessão já leu, e quem concede depois do pagamento
 * é o webhook do provedor. A tela abre o checkout e espera: quando o direito
 * chega, ele chega pelo socket em `user.entitlements`, e a sessão atualiza
 * sozinha. Por isso não existe aqui nenhuma tentativa de adivinhar a compra.
 *
 * A terceira é o **dinheiro de verdade**. O `Money` dos átomos formata a moeda
 * do jogo, que é o `$` genérico do mundo fictício. Preço de loja é real, vem em
 * centavos e com a moeda declarada pelo servidor, então tem formatação própria,
 * logo abaixo.
 *
 * A quarta chegou com os cosméticos: **comprar e vestir são coisas separadas**.
 * O vestiário mora nesta mesma tela porque é aqui que a pessoa acabou de
 * comprar, e quem leva quatro escudos precisa poder alternar entre eles sem
 * caçar a opção em outro canto. A troca vai para o servidor, que é quem confere
 * o direito; o cliente só desenha o que voltou.
 */

const POR_PAGINA = 20;

/** Moeda pedida no checkout. O Pix só existe em real, e o catálogo é semeado nele. */
const MOEDA = "BRL";

/** Ordem em que os grupos aparecem, do compromisso maior para o menor. */
const GRUPOS: ReadonlyArray<{ chave: Chave; tipos: readonly TipoDeProduto[] }> = [
  { chave: "loja.grupos.lifetime", tipos: ["lifetime"] },
  { chave: "loja.grupos.passe", tipos: ["pass_month", "pass_year"] },
  { chave: "loja.grupos.cosmetic", tipos: ["cosmetic"] },
  { chave: "loja.grupos.datapack", tipos: ["datapack"] },
];

/**
 * Preço em centavos, na moeda que o servidor declarou.
 *
 * Fora do i18n de propósito: `dinheiro()` de lá é a moeda do jogo, que não tem
 * país nem centavo. Aqui é real, euro ou dólar, e o símbolo certo vem do
 * próprio `Intl`. A etiqueta de idioma serve como etiqueta de local porque hoje
 * as duas coincidem, e um idioma novo que mude isso vai mexer no i18n inteiro.
 */
function preco(centavos: number, moeda: string, idioma: Idioma): string {
  try {
    return new Intl.NumberFormat(idioma, { style: "currency", currency: moeda }).format(
      centavos / 100,
    );
  } catch {
    // Moeda fora da lista do `Intl`. Melhor um número honesto que uma tela vazia.
    return `${moeda} ${(centavos / 100).toFixed(2)}`;
  }
}

/** O 503 de servidor sem credencial, que não é erro de uso. */
function semPagamento(causa: unknown): causa is ErroDaApi {
  return causa instanceof ErroDaApi && causa.code === "BILLING_NOT_CONFIGURED";
}

/** O meio que o 503 recusou, para o aviso dizer qual foi. */
function metodoDoErro(causa: ErroDaApi): string | null {
  const metodo = causa.details?.["method"];
  return typeof metodo === "string" ? metodo : null;
}

export function Loja({ aoVoltar }: { aoVoltar?: () => void }): ReactNode {
  const t = useT();
  const sessao = useSessao();

  const [catalogo, definirCatalogo] = useState<Catalogo | null>(null);
  const [cobranca, definirCobranca] = useState<Cobranca | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [aviso, definirAviso] = useState<string | null>(null);
  /** Texto do 503, guardado à parte porque não é erro e não some ao recarregar. */
  const [indisponivel, definirIndisponivel] = useState<string | null>(null);
  const [ocupado, definirOcupado] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      definirCatalogo(
        await sessao.cliente.pedir(
          { metodo: "GET", caminho: "/v1/billing/products" },
          CatalogoSchema,
        ),
      );
      definirErro(null);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
      definirCatalogo((atual) => atual ?? { items: [], methods: { card: false, pix: false } });
    }
  }, [sessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /*
   * O catálogo já diz quais meios existem, então o aviso de servidor sem
   * pagamento aparece antes de alguém clicar em comprar e levar um 503 na cara.
   */
  const semMeio = catalogo !== null && !catalogo.methods.card && !catalogo.methods.pix;

  const comprar = async (produto: ProdutoDaLoja, metodo: MetodoDePagamento): Promise<void> => {
    definirErro(null);
    definirAviso(null);
    definirOcupado(`${produto.id}:${metodo}`);
    try {
      const nova = await sessao.cliente.pedir(
        {
          metodo: "POST",
          caminho: "/v1/billing/checkout",
          corpo: { productId: produto.id, method: metodo, currency: MOEDA },
        },
        CobrancaSchema,
      );
      definirCobranca(nova);
      definirIndisponivel(null);

      /*
       * Cartão é checkout hospedado pelo provedor, então o lugar dele é uma aba
       * nova: dentro do jogo a pessoa perderia o histórico de navegação e o
       * botão de voltar do banco. Quando o navegador bloqueia a aba, o painel
       * de cobrança mostra o endereço para ela abrir na mão.
       */
      if (nova.checkoutUrl) {
        const aberta = await abrirFora(nova.checkoutUrl);
        definirAviso(aberta ? t("loja.checkoutAberto") : t("loja.checkoutSemAba"));
      }
    } catch (causa) {
      if (semPagamento(causa)) {
        const qual = metodoDoErro(causa);
        definirIndisponivel(
          qual ? t("loja.semPagamentoMetodo", { metodo: nomeDoMetodo(qual, t) }) : null,
        );
        return;
      }
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(null);
    }
  };

  const produtos = catalogo?.items ?? [];
  const direitos = sessao.conta?.direitos ?? null;

  return (
    <Pagina titulo={t("loja.titulo")} aoVoltar={aoVoltar}>
      {aviso && (
        <p role="status" style={{ margin: 0, color: "var(--primary)", fontSize: 12.5 }}>
          {aviso}
        </p>
      )}

      {(semMeio || indisponivel !== null) && <SemPagamento detalhe={indisponivel} />}

      <Assinatura />

      <Vestiario />

      {cobranca && <PainelDeCobranca cobranca={cobranca} aoFechar={() => definirCobranca(null)} />}

      <Panel
        title={t("loja.catalogo")}
        action={
          <button
            className="ghost"
            onClick={() => void carregar()}
            style={{ minHeight: 26, padding: "2px 8px", fontSize: 11 }}
          >
            {t("loja.atualizar")}
          </button>
        }
      >
        {catalogo === null && (
          <p className="muted" style={{ margin: 0 }}>
            {t("online.carregando")}
          </p>
        )}
        {catalogo !== null && produtos.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            {t("loja.semProdutos")}
          </p>
        )}

        <div style={{ display: "grid", gap: 16 }}>
          {GRUPOS.map((grupo) => {
            const doGrupo = produtos.filter((produto) => grupo.tipos.includes(produto.kind));
            if (doGrupo.length === 0) return null;
            return (
              <div key={grupo.chave} style={{ display: "grid", gap: 10 }}>
                <span className="label">{t(grupo.chave)}</span>
                {doGrupo.map((produto) => (
                  <LinhaDeProduto
                    key={produto.id}
                    produto={produto}
                    metodos={catalogo?.methods ?? { card: false, pix: false }}
                    jaTem={
                      direitos !== null &&
                      ((produto.kind === "lifetime" && direitos.lifetime) ||
                        (produto.kind === "cosmetic" && direitos.cosmetics.includes(produto.id)))
                    }
                    ocupado={ocupado}
                    aoComprar={(metodo) => void comprar(produto, metodo)}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </Panel>

      <Recibos />
    </Pagina>
  );
}

// ---------------------------------------------------------------------------
// Servidor sem provedor de pagamento
// ---------------------------------------------------------------------------

/**
 * Recado de instalação sem credencial.
 *
 * Fica na cor de aviso e não na de erro, e usa `role="status"` e não
 * `role="alert"`: quem lê por leitor de tela também merece saber que isto é
 * informação e não falha.
 */
function SemPagamento({ detalhe }: { detalhe: string | null }): ReactNode {
  const t = useT();
  return (
    <div
      role="status"
      style={{
        padding: "10px 12px",
        border: "1px solid var(--warning)",
        borderRadius: "var(--radius-sm)",
        background: "var(--surface-2)",
      }}
    >
      <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{t("loja.semPagamento")}</p>
      <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>
        {t("loja.semPagamentoCorpo")}
      </p>
      {detalhe && (
        <p className="muted" style={{ margin: "4px 0 0", fontSize: 11.5 }}>
          {detalhe}
        </p>
      )}
    </div>
  );
}

/** Nome do meio de pagamento que o 503 nomeou, quando ele é um dos conhecidos. */
function nomeDoMetodo(metodo: string, t: (chave: Chave) => string): string {
  const conhecidos = ["card", "pix", "android", "ios"] as const;
  const achado = conhecidos.find((qual) => qual === metodo);
  return achado ? t(`loja.metodos.${achado}`) : metodo;
}

// ---------------------------------------------------------------------------
// Assinatura
// ---------------------------------------------------------------------------

/**
 * Passe ativo, cancelamento da renovação e restauração de compras.
 *
 * A restauração aqui relê `GET /v1/me` pela sessão. Não é atalho: o direito
 * mora no servidor, então conferir com ele é literalmente o que restaurar
 * significa para uma compra feita no site ou em outro aparelho.
 */
function Assinatura(): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [ocupado, definirOcupado] = useState<"cancelar" | "restaurar" | null>(null);
  const [aviso, definirAviso] = useState<string[]>([]);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);

  const passe = sessao.conta?.direitos.pass ?? null;

  const cancelar = async (): Promise<void> => {
    definirErro(null);
    definirAviso([]);
    definirOcupado("cancelar");
    try {
      /*
       * O corpo vazio é de propósito, e não é enfeite. O cliente da API manda
       * `Content-Type: application/json` em todo pedido, e um POST que anuncia
       * JSON e não manda nada faz este servidor responder 500 em vez de 400.
       * Mandar `{}` é o que separa cancelar de tomar erro genérico.
       */
      const feito = await sessao.cliente.pedir(
        { metodo: "POST", caminho: "/v1/billing/cancel", corpo: {} },
        CancelamentoDeAssinaturaSchema,
      );
      const linhas = [t("loja.cancelado", { total: feito.canceled })];
      if (feito.activeUntil) {
        linhas.push(t("loja.canceladoAte", { data: dataLonga(feito.activeUntil) }));
      }
      if (feito.manageInStore) linhas.push(t("loja.cancelarNaLoja"));
      definirAviso(linhas);
      await sessao.recarregarConta();
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(null);
    }
  };

  const restaurar = async (): Promise<void> => {
    definirErro(null);
    definirAviso([]);
    definirOcupado("restaurar");
    try {
      await sessao.recarregarConta();
      definirAviso([t("loja.restaurado")]);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(null);
    }
  };

  return (
    <Panel title={t("loja.assinatura")}>
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}
      >
        <Stat label={t("conta.vitalicia")}>
          {sessao.conta?.direitos.lifetime ? t("conta.temDireito") : t("conta.semDireito")}
        </Stat>
        <Stat label={t("conta.passe")}>
          {passe?.active ? t("conta.temDireito") : t("conta.semDireito")}
        </Stat>
        <Stat label={t("conta.cosmeticos")}>
          {t("conta.cosmeticosTotal", { total: sessao.conta?.direitos.cosmetics.length ?? 0 })}
        </Stat>
      </div>

      <p className="muted" style={{ margin: "10px 0 0", fontSize: 11.5 }}>
        {passe?.active
          ? `${t("loja.assinaturaAtiva")} ${
              passe.renewsAt
                ? t("loja.assinaturaRenova", { data: dataLonga(passe.renewsAt) })
                : t("loja.assinaturaSemRenovacao")
            }`
          : t("loja.assinaturaSemPasse")}
      </p>

      {aviso.map((linha) => (
        <p key={linha} style={{ margin: "8px 0 0", color: "var(--primary)", fontSize: 12.5 }}>
          {linha}
        </p>
      ))}

      <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button
          className="ghost"
          disabled={ocupado !== null || !passe?.active}
          onClick={() => void cancelar()}
        >
          {ocupado === "cancelar" ? t("loja.cancelando") : t("loja.cancelar")}
        </button>
        <button className="ghost" disabled={ocupado !== null} onClick={() => void restaurar()}>
          {ocupado === "restaurar" ? t("loja.restaurando") : t("loja.restaurar")}
        </button>
      </div>

      <p className="muted" style={{ margin: "8px 0 0", fontSize: 11 }}>
        {t("loja.cancelarAjuda")}
      </p>
      <p className="muted" style={{ margin: "4px 0 0", fontSize: 11 }}>
        {t("loja.restaurarAjuda")}
      </p>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Vestiário
// ---------------------------------------------------------------------------

/** Ordem dos tipos, do que aparece maior na tela para o que aparece menor. */
const TIPOS: readonly CosmeticKind[] = ["crest", "kit", "stadium", "title"];

/**
 * Onde a pessoa escolhe o que está usando.
 *
 * Vender cinco escudos sem deixar trocar entre eles seria vender coleção, então
 * a troca é livre, imediata e sem custo. O botão manda só o campo que mudou:
 * trocar de escudo não pode apagar o título que já estava posto.
 *
 * A lista é relida quando o número de cosméticos da conta muda, que é o sinal
 * que a sessão recebe pelo socket quando uma compra é confirmada. É por isso
 * que o item recém-comprado aparece aqui sem ninguém apertar atualizar.
 */
function Vestiario(): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [meus, definirMeus] = useState<MyCosmetics | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [aviso, definirAviso] = useState<string | null>(null);
  const [ocupado, definirOcupado] = useState<string | null>(null);

  const quantos = sessao.conta?.direitos.cosmetics.length ?? 0;

  const carregar = useCallback(async () => {
    try {
      definirMeus(
        await sessao.cliente.pedir({ metodo: "GET", caminho: "/v1/me/cosmetics" }, MyCosmeticsSchema),
      );
      definirErro(null);
    } catch {
      // A loja inteira não pode cair por causa do vestiário, então o recado é
      // curto e o resto da tela continua de pé.
      definirErro(t("loja.vestiarioErro"));
    }
  }, [sessao, t]);

  useEffect(() => {
    void carregar();
  }, [carregar, quantos]);

  /*
   * `qual` identifica o cartão clicado, e não o destino da troca: tirar manda
   * `null` e mesmo assim é o cartão em uso que precisa mostrar que está
   * esperando resposta.
   */
  const trocar = async (qual: string, tipo: CosmeticKind, sku: string | null): Promise<void> => {
    definirErro(null);
    definirAviso(null);
    definirOcupado(qual);
    try {
      definirMeus(
        await sessao.cliente.pedir(
          { metodo: "PUT", caminho: "/v1/me/cosmetics", corpo: { [tipo]: sku } },
          MyCosmeticsSchema,
        ),
      );
      definirAviso(t("loja.trocado"));
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(null);
    }
  };

  return (
    <Panel title={t("loja.vestiario")}>
      <p className="muted" style={{ margin: "0 0 12px", fontSize: 12 }}>
        {t("loja.vestiarioAjuda")}
      </p>

      {aviso && (
        <p role="status" style={{ margin: "0 0 10px", color: "var(--primary)", fontSize: 12.5 }}>
          {aviso}
        </p>
      )}

      {meus === null && erro === null && (
        <p className="muted" style={{ margin: 0 }}>
          {t("online.carregando")}
        </p>
      )}
      {meus !== null && meus.owned.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          {t("loja.vestiarioVazio")}
        </p>
      )}

      <div style={{ display: "grid", gap: 16 }}>
        {TIPOS.map((tipo) => {
          const doTipo = (meus?.owned ?? []).filter((item) => item.kind === tipo);
          if (doTipo.length === 0) return null;
          return (
            <div key={tipo} style={{ display: "grid", gap: 8 }}>
              <span className="label">{t(`cosmeticos.tipos.${tipo}`)}</span>
              <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
                {t(`loja.ondeAparece.${tipo}`)}
              </p>
              <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                {doTipo.map((item) => (
                  <CartaoDoVestiario
                    key={item.sku}
                    item={item}
                    ocupado={ocupado !== null}
                    trocando={ocupado === item.sku}
                    aoTrocar={() =>
                      void trocar(item.sku, tipo, item.equipped ? null : item.sku)
                    }
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/**
 * Um item da coleção, com a prévia desenhada.
 *
 * O item em uso fica com a borda acesa e o botão vira "tirar", porque quem
 * comprou um visual também precisa do caminho de volta para o escudo de sempre.
 */
function CartaoDoVestiario({
  item,
  ocupado,
  trocando,
  aoTrocar,
}: {
  item: OwnedCosmetic;
  ocupado: boolean;
  trocando: boolean;
  aoTrocar: () => void;
}): ReactNode {
  const t = useT();
  return (
    <div
      style={{
        display: "grid",
        gap: 6,
        justifyItems: "center",
        width: 132,
        padding: "10px 8px",
        border: `1px solid ${item.equipped ? "var(--primary)" : "var(--border)"}`,
        borderRadius: "var(--radius-sm)",
        background: item.equipped ? "var(--primary-glow)" : "var(--surface-2)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", minHeight: 56 }}>
        <PreviaDeCosmetico params={item.params} tamanho={44} />
      </div>
      <span style={{ fontSize: 11.5, fontWeight: 700, textAlign: "center" }}>{item.name}</span>
      <button
        className={item.equipped ? "ghost" : "primary"}
        disabled={ocupado}
        onClick={aoTrocar}
        style={{ minHeight: 26, padding: "2px 10px", fontSize: 11 }}
      >
        {trocando
          ? t("loja.trocando")
          : item.equipped
            ? t("loja.tirar")
            : t("loja.usar")}
      </button>
      {item.equipped && <span className="label">{t("loja.emUso")}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------

function LinhaDeProduto({
  produto,
  metodos,
  jaTem,
  ocupado,
  aoComprar,
}: {
  produto: ProdutoDaLoja;
  metodos: { card: boolean; pix: boolean };
  jaTem: boolean;
  /** Chave do botão que está esperando resposta, no formato `produto:meio`. */
  ocupado: string | null;
  aoComprar: (metodo: MetodoDePagamento) => void;
}): ReactNode {
  const t = useT();
  const { idioma } = useIdioma();
  const centavos = produto.prices[MOEDA];

  return (
    <div
      className="row"
      style={{
        gap: 12,
        alignItems: "flex-start",
        padding: "10px 12px",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        background: "var(--surface-2)",
      }}
    >
      {/* O cosmético mostra o que ele é, e não uma caixa cinza com o nome
          dentro. Os parâmetros vêm do servidor, então a prévia é o item. */}
      {produto.cosmetic && (
        <div style={{ display: "flex", alignItems: "center", minHeight: 52 }}>
          <PreviaDeCosmetico params={produto.cosmetic.params} tamanho={46} />
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700 }}>{produto.name}</span>
          <span className="label-muted">
            {produto.cosmetic
              ? t(`cosmeticos.tipos.${produto.cosmetic.kind}`)
              : t(`loja.tipos.${produto.kind}`)}
          </span>
          {jaTem && <span className="label">{t("loja.jaTem")}</span>}
        </div>
        {produto.cosmetic && (
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            {produto.cosmetic.description}
          </div>
        )}
        <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
          {/* O dicionário não tem plural automático, e "1 meses" é justamente o
              tipo de erro que faz a loja parecer descuidada. */}
          {produto.durationMonths === null
            ? t("loja.semPrazo")
            : produto.durationMonths === 1
              ? t("loja.duracaoUmMes")
              : t("loja.duracaoMeses", { meses: produto.durationMonths })}
        </div>
        <div className="num" style={{ fontSize: 16, fontWeight: 800, marginTop: 4 }}>
          {centavos === undefined
            ? t("loja.semPreco", { moeda: MOEDA })
            : preco(centavos, MOEDA, idioma)}
        </div>
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <BotaoDeCompra
          rotulo={t("loja.comprarCartao")}
          disponivel={metodos.card}
          foraDoAr={t("loja.metodoFora", { metodo: t("loja.metodos.card") })}
          ocupado={ocupado === `${produto.id}:card`}
          bloqueado={ocupado !== null || centavos === undefined || jaTem}
          aoClicar={() => aoComprar("card")}
        />
        <BotaoDeCompra
          rotulo={t("loja.comprarPix")}
          disponivel={metodos.pix}
          foraDoAr={t("loja.metodoFora", { metodo: t("loja.metodos.pix") })}
          ocupado={ocupado === `${produto.id}:pix`}
          bloqueado={ocupado !== null || centavos === undefined || jaTem}
          aoClicar={() => aoComprar("pix")}
        />
      </div>
    </div>
  );
}

/**
 * Botão de um meio de pagamento.
 *
 * O meio que este servidor não oferece continua na tela, apagado e com o motivo
 * no título. Sumir com ele deixaria a pessoa procurando o Pix que ela usa
 * sempre, sem nunca descobrir que ele não existe aqui.
 */
function BotaoDeCompra({
  rotulo,
  disponivel,
  foraDoAr,
  ocupado,
  bloqueado,
  aoClicar,
}: {
  rotulo: string;
  disponivel: boolean;
  foraDoAr: string;
  ocupado: boolean;
  bloqueado: boolean;
  aoClicar: () => void;
}): ReactNode {
  const t = useT();
  return (
    <button
      className={disponivel ? "primary" : "ghost"}
      disabled={!disponivel || bloqueado}
      title={disponivel ? undefined : foraDoAr}
      onClick={aoClicar}
      style={{ minHeight: 30, padding: "3px 12px", fontSize: 12, whiteSpace: "nowrap" }}
    >
      {ocupado ? t("loja.abrindo") : rotulo}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Cobrança aberta
// ---------------------------------------------------------------------------

function PainelDeCobranca({
  cobranca,
  aoFechar,
}: {
  cobranca: Cobranca;
  aoFechar: () => void;
}): ReactNode {
  const t = useT();
  const { idioma } = useIdioma();
  const [copiado, definirCopiado] = useState(false);

  const valor = preco(cobranca.amountCents, cobranca.currency, idioma);

  return (
    <Panel
      title={cobranca.pix ? t("loja.pixTitulo", { valor }) : cobranca.sku}
      action={
        <button
          className="ghost"
          onClick={aoFechar}
          style={{ minHeight: 26, padding: "2px 8px", fontSize: 11 }}
        >
          {t("loja.fecharCobranca")}
        </button>
      }
    >
      {cobranca.checkoutUrl && (
        <a
          className="ghost"
          href={cobranca.checkoutUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            padding: "6px 14px",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--border-strong)",
            textDecoration: "none",
          }}
        >
          {t("loja.abrirPagamento")}
        </a>
      )}

      {cobranca.pix && (
        <div style={{ display: "grid", gap: 10, justifyItems: "start" }}>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            {t("loja.pixInstrucao")}
          </p>
          {cobranca.pix.qrCode && (
            <img
              src={
                cobranca.pix.qrCode.startsWith("data:")
                  ? cobranca.pix.qrCode
                  : `data:image/png;base64,${cobranca.pix.qrCode}`
              }
              alt={t("loja.pixTitulo", { valor })}
              width={180}
              height={180}
              style={{ borderRadius: "var(--radius-sm)", background: "#fff", padding: 6 }}
            />
          )}
          <code
            style={{
              display: "block",
              width: "100%",
              wordBreak: "break-all",
              fontSize: 11,
              padding: 8,
              borderRadius: "var(--radius-sm)",
              background: "var(--surface-3)",
            }}
          >
            {cobranca.pix.copyPaste}
          </code>
          <button
            className="ghost"
            onClick={() => {
              const codigo = cobranca.pix?.copyPaste ?? "";
              void navigator.clipboard.writeText(codigo).then(() => definirCopiado(true));
            }}
          >
            {copiado ? t("loja.pixCopiado") : t("loja.pixCopiar")}
          </button>
          {cobranca.pix.expiresAt && (
            <p className="muted" style={{ margin: 0, fontSize: 11 }}>
              {t("loja.pixExpira", { data: dataEHora(cobranca.pix.expiresAt) })}
            </p>
          )}
        </div>
      )}

      <p className="muted" style={{ margin: "10px 0 0", fontSize: 11 }}>
        {t("loja.pixEspera")}
      </p>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Recibos
// ---------------------------------------------------------------------------

function Recibos(): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const { idioma } = useIdioma();
  const [itens, definirItens] = useState<Recibo[] | null>(null);
  const [cursor, definirCursor] = useState<string | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [ocupado, definirOcupado] = useState(false);

  const carregar = useCallback(
    async (apartirDe: string | null) => {
      definirOcupado(true);
      definirErro(null);
      try {
        const busca = new URLSearchParams({ limit: String(POR_PAGINA) });
        if (apartirDe) busca.set("cursor", apartirDe);
        const pagina = await sessao.cliente.pedir(
          { metodo: "GET", caminho: `/v1/billing/invoices?${busca.toString()}` },
          PaginaDeRecibosSchema,
        );
        definirItens((atuais) => (apartirDe ? [...(atuais ?? []), ...pagina.items] : pagina.items));
        definirCursor(pagina.nextCursor);
      } catch (causa) {
        definirErro(mensagemDoErro(causa));
        definirItens((atuais) => atuais ?? []);
      } finally {
        definirOcupado(false);
      }
    },
    [sessao],
  );

  useEffect(() => {
    void carregar(null);
  }, [carregar]);

  return (
    <Panel title={t("loja.recibos")}>
      {itens === null && (
        <p className="muted" style={{ margin: 0 }}>
          {t("online.carregando")}
        </p>
      )}
      {itens !== null && itens.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          {t("loja.semRecibos")}
        </p>
      )}

      <div className="grid" style={{ gap: 8 }}>
        {(itens ?? []).map((recibo) => (
          <div key={recibo.id} className="row" style={{ gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{recibo.name}</div>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {t("loja.reciboLinha", {
                  situacao: t(`loja.situacoes.${recibo.status}`),
                  /* A lista de origens é a mesma da tela de conta. Repetir a
                     redação aqui só criaria duas versões do mesmo texto. */
                  origem: t(`conta.origens.${recibo.source}`),
                  data: dataEHora(recibo.createdAt),
                })}
              </div>
            </div>
            <span className="num" style={{ fontWeight: 700 }}>
              {preco(recibo.amountCents, recibo.currency, idioma)}
            </span>
          </div>
        ))}
      </div>

      {cursor && (
        <button
          className="ghost"
          disabled={ocupado}
          onClick={() => void carregar(cursor)}
          style={{ marginTop: 10 }}
        >
          {t("loja.carregarMais")}
        </button>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Peças de tela
// ---------------------------------------------------------------------------

